import React, { FC } from 'react';
import { Formik } from 'formik';
import { Input, SubmitButton } from 'formik-antd/lib';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import zxcvbn from 'zxcvbn';
import { LockOutlined } from '@ant-design/icons';
import getValidationSchema, { INITIAL_VALUES, Values } from './ActivationStudentForm.schema';
import { getIsActivatingAccount } from '../../../slice/selectors';
import styles from './ActivationStudentForm.module.less';
import PasswordStrengthMeter from '../../../../../components/PasswordStrengthMeter';
import FormItem from '../../../../../components/FormItem';

const { Text } = Typography;

type ActivationStudentFormProps = {
  onSubmit: (values: Values) => void;
  // „Aktywuj konto” przy aktywacji, „Zapisz hasło” przy linku do nowego hasła
  submitLabel: string;
};

const ActivationStudentForm: FC<ActivationStudentFormProps> = (
  { onSubmit, submitLabel }: ActivationStudentFormProps,
) => {
  const { t } = useTranslation('common');
  const isActivating = useSelector(getIsActivatingAccount);

  const validatePasswordStrength = (password: string): string | undefined => {
    const { score } = zxcvbn(password);

    return score < 2 ? t('validation.password.tooWeak') : undefined;
  };

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={getValidationSchema(t)}
      initialValues={INITIAL_VALUES}
    >
      {({
        handleSubmit, values, errors, touched,
      }) => (
        <form onSubmit={handleSubmit} className={styles.form} data-cy="activationForm">
          {/* Wspólne pole jak przy logowaniu — ten sam wygląd co pozostałe pola */}
          <FormItem
            name="password"
            placeholderKey="common:activation.placeholders.password"
            validate={validatePasswordStrength}
            Component={Input.Password}
            componentAdditionalProps={{ prefix: <LockOutlined /> }}
            error={errors.password}
            isTouched={touched.password}
            value={values.password}
          />
          <PasswordStrengthMeter password={values.password} onLinen />
          <Text className={styles.reminder}>{t('passwordStrength.reminder')}</Text>
          <SubmitButton
            disabled={isActivating}
            loading={isActivating}
            className={styles.button}
            size="large"
            block
            style={{ borderRadius: '6px' }}
          >
            {submitLabel}
          </SubmitButton>
        </form>
      )}
    </Formik>
  );
};

export default ActivationStudentForm;
